// Typed API client used by both web (fetch with credentials: include) and
// mobile (fetch + manual X-Refresh-Token). Emits X-EDV-Client and
// X-Correlation-Id, propagates Authorization: Bearer, and never writes
// tokens to browser storage or logs.

import type {
  Asset,
  AssetListResponse,
  AssetMutationResult,
  AssetPatchRequest,
  AssetLifecycleRequest,
  AssetWriteRequest,
  BootstrapOrganizationRequest,
  BootstrapOrganizationResponse,
  LoginRequest,
  LoginResponseMobile,
  LoginResponseWeb,
  MutationEnvelope,
  PublicBrandingResponse,
  SessionMe,
  SyncCheckpointAdvanceRequest,
  SyncCheckpointAdvanceResponse,
  SyncCheckpointRead,
  SyncPullRequest,
  SyncPullResponse,
  SyncPushRequest,
  SyncPushResponse,
} from './index.js';

export type ClientKind = 'web' | 'mobile';

export interface ApiClientConfig {
  baseUrl: string;
  clientKind: ClientKind;
  origin?: string; // web only, used for CORS-safe preflight
  fetchImpl?: typeof fetch;
  getAccessJwt?: () => string | null;
  getRefreshToken?: () => string | null; // mobile
  getDeviceId?: () => string | null;
  correlationId?: () => string;
}

/**
 * H9-R4: shared typed network failure. Used by createApiClient to convert a
 * rejected fetch() invocation into an explicit, catchable class that mobile
 * transport-result maps to `offline`. Preserves HTTP responses as
 * ApiError-classified failures.
 */
/**
 * H10-R6: shared typed schema-response failure. Emitted by createApiClient
 * when a syntactically-valid HTTP response body cannot be parsed as JSON, or
 * when contract validation fails downstream. Distinct from ApiError so
 * mobile transport can map it to `SCHEMA_INVALID_RESPONSE`.
 */
export class ApiSchemaError extends Error {
  readonly kind = 'schema' as const;
  constructor(message?: string) {
    super(message ?? 'response did not match contract');
    this.name = 'ApiSchemaError';
  }
}

export class ApiNetworkError extends Error {
  readonly kind = 'network' as const;
  constructor(message?: string) {
    super(message ?? 'network transport failure');
    this.name = 'ApiNetworkError';
  }
}

export class ApiError extends Error {
  code: string;
  status: number;
  correlationId: string;
  detail?: unknown;
  constructor(
    status: number,
    code: string,
    message: string,
    correlationId: string,
    detail?: unknown,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
    this.detail = detail;
  }
}

function randCid(): string {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    (globalThis.crypto as { randomUUID?: () => string }).randomUUID
  ) {
    return (globalThis.crypto as { randomUUID: () => string }).randomUUID();
  }
  return 'cid-' + Math.random().toString(36).slice(2);
}

export function createApiClient(cfg: ApiClientConfig) {
  const f = cfg.fetchImpl ?? fetch;
  const cid = cfg.correlationId ?? randCid;

  async function raw(path: string, init: RequestInit & { auth?: boolean } = {}): Promise<Response> {
    const headers = new Headers(init.headers || {});
    headers.set('X-EDV-Client', cfg.clientKind);
    const deviceId = cfg.getDeviceId?.();
    if (deviceId) headers.set('X-EDV-Device-ID', deviceId);
    if (!headers.has('X-Correlation-Id')) headers.set('X-Correlation-Id', cid());
    if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
    if (init.auth !== false) {
      const jwt = cfg.getAccessJwt ? cfg.getAccessJwt() : null;
      if (jwt) headers.set('Authorization', 'Bearer ' + jwt);
    }
    const url = cfg.baseUrl.replace(/\/$/, '') + path;
    try {
      return await f(url, {
        ...init,
        headers,
        credentials: cfg.clientKind === 'web' ? 'include' : 'omit',
      });
    } catch (e) {
      // H9-R4: real fetch rejection (offline / CORS-blocked / DNS) is a
      // network failure — surface a shared typed class so mobile transport
      // can classify it as `offline` without inspecting error names.
      throw new ApiNetworkError((e as Error)?.message);
    }
  }

  async function json<T>(path: string, init: RequestInit & { auth?: boolean } = {}): Promise<T> {
    const res = await raw(path, init);
    const bodyText = await res.text();
    let body: unknown;
    try {
      body = bodyText ? JSON.parse(bodyText) : {};
    } catch {
      throw new ApiSchemaError('malformed JSON in response body');
    }
    if (!res.ok) {
      const err = body as {
        error?: { code?: string; message?: string; detail?: unknown };
        correlation_id?: string;
      };
      throw new ApiError(
        res.status,
        err.error?.code || 'INTERNAL_ERROR',
        err.error?.message || 'request failed',
        err.correlation_id || 'unknown',
        err.error?.detail,
      );
    }
    return body as T;
  }

  return {
    raw,
    json,

    // auth
    async login(body: LoginRequest, headers: Record<string, string> = {}) {
      return json<LoginResponseWeb | LoginResponseMobile>('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify(body),
        headers,
        auth: false,
      });
    },
    async refresh() {
      const headers: Record<string, string> = {};
      if (cfg.clientKind === 'mobile' && cfg.getRefreshToken) {
        const rt = cfg.getRefreshToken();
        if (rt) headers['X-Refresh-Token'] = rt;
      }
      return json<LoginResponseWeb | LoginResponseMobile>('/api/v1/auth/refresh', {
        method: 'POST',
        headers,
        auth: false,
      });
    },
    async logout() {
      const headers: Record<string, string> = {};
      if (cfg.clientKind === 'mobile' && cfg.getRefreshToken) {
        const rt = cfg.getRefreshToken();
        if (rt) headers['X-Refresh-Token'] = rt;
      }
      const res = await raw('/api/v1/auth/logout', { method: 'POST', headers, auth: false });
      if (res.status !== 204 && !res.ok) {
        const t = await res.text();
        throw new ApiError(res.status, 'INTERNAL_ERROR', t || 'logout failed', 'unknown');
      }
    },

    // session
    async sessionMe() {
      return json<SessionMe>('/api/v1/session/me', { method: 'GET' });
    },

    // branding
    async publicBranding() {
      return json<PublicBrandingResponse>('/api/v1/public/branding', {
        method: 'GET',
        auth: false,
      });
    },

    // assets
    async listAssets(
      siteId: string,
      opts: { cursor?: string; limit?: number; query?: string } = {},
    ) {
      const qs = new URLSearchParams();
      if (opts.cursor) qs.set('cursor', opts.cursor);
      if (opts.limit != null) qs.set('limit', String(opts.limit));
      if (opts.query) qs.set('query', opts.query);
      const s = qs.toString();
      return json<AssetListResponse>(
        `/api/v1/sites/${encodeURIComponent(siteId)}/assets${s ? '?' + s : ''}`,
        { method: 'GET' },
      );
    },
    async createAsset(siteId: string, body: AssetWriteRequest) {
      return json<AssetMutationResult>(`/api/v1/sites/${encodeURIComponent(siteId)}/assets`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Idempotency-Key': body.mutation_id },
      });
    },
    async getAsset(assetId: string) {
      return json<Asset>(`/api/v1/assets/${encodeURIComponent(assetId)}`, { method: 'GET' });
    },
    async patchAsset(assetId: string, body: AssetPatchRequest) {
      return json<AssetMutationResult>(`/api/v1/assets/${encodeURIComponent(assetId)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: { 'Idempotency-Key': body.mutation_id },
      });
    },
    async archiveAsset(assetId: string, body: AssetLifecycleRequest) {
      return json<AssetMutationResult>(`/api/v1/assets/${encodeURIComponent(assetId)}/archive`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Idempotency-Key': body.mutation_id },
      });
    },
    async restoreAsset(assetId: string, body: AssetLifecycleRequest) {
      return json<AssetMutationResult>(`/api/v1/assets/${encodeURIComponent(assetId)}/restore`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Idempotency-Key': body.mutation_id },
      });
    },

    // sync
    async syncPush(mutations: MutationEnvelope[]) {
      const req: SyncPushRequest = { mutations };
      return json<SyncPushResponse>('/api/v1/sync/push', {
        method: 'POST',
        body: JSON.stringify(req),
      });
    },
    async syncPull(req: SyncPullRequest) {
      return json<SyncPullResponse>('/api/v1/sync/pull', {
        method: 'POST',
        body: JSON.stringify(req),
      });
    },
    async syncCheckpointRead(siteId: string) {
      return json<SyncCheckpointRead>(
        `/api/v1/sync/checkpoint?site_id=${encodeURIComponent(siteId)}`,
        { method: 'GET' },
      );
    },
    async syncCheckpointAdvance(req: SyncCheckpointAdvanceRequest) {
      return json<SyncCheckpointAdvanceResponse>('/api/v1/sync/checkpoint', {
        method: 'POST',
        body: JSON.stringify(req),
      });
    },

    // admin (never called from user apps; the admin bundle uses this directly)
    async bootstrapOrganization(req: BootstrapOrganizationRequest, cfAssertion: string) {
      return json<BootstrapOrganizationResponse>('/admin/organizations/bootstrap', {
        method: 'POST',
        body: JSON.stringify(req),
        headers: { 'Cf-Access-Jwt-Assertion': cfAssertion },
        auth: false,
      });
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
